import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  EnvelopeOpen, ArrowsClockwise, FileArrowDown, Warning, CheckCircle, FunnelSimple,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATUS_META = {
  imported: { label: "Importado", bg: "bg-green-100 text-green-800", icon: CheckCircle },
  "no-pdf": { label: "Sin PDF", bg: "bg-amber-100 text-amber-800", icon: Warning },
  duplicate: { label: "Duplicado", bg: "bg-stone-100 text-stone-700", icon: ArrowsClockwise },
};

export default function EmailIntake() {
  const [log, setLog] = useState([]);
  const [rules, setRules] = useState([]);
  const [filter, setFilter] = useState("all");
  const [polling, setPolling] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [logR, rulesR] = await Promise.all([
        api.get("/settings/imap/intake/log?limit=100"),
        api.get("/settings/imap/intake/rules"),
      ]);
      setLog(logR.data || []);
      setRules(rulesR.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerIntake = async () => {
    setPolling(true);
    try {
      const r = await api.post("/settings/imap/intake");
      const created = r.data?.expedientes_created || 0;
      const scanned = r.data?.scanned || 0;
      if (!r.data?.ok) {
        toast.error(r.data?.error || "Error");
      } else if (created > 0) {
        toast.success(`✓ ${created} expediente(s) creado(s) desde ${scanned} correo(s) escaneado(s)`);
      } else {
        toast.message(`Sincronización completada · ${scanned} correo(s) escaneado(s)`);
      }
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setPolling(false);
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return log;
    return log.filter((l) => (l.status || "imported") === filter);
  }, [log, filter]);

  const stats = useMemo(() => {
    const out = { total: log.length, imported: 0, "no-pdf": 0, duplicate: 0 };
    log.forEach((l) => { out[l.status || "imported"] = (out[l.status || "imported"] || 0) + 1; });
    return out;
  }, [log]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="email-intake-page">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <EnvelopeOpen className="w-7 h-7 text-brand" weight="duotone" />
          <div>
            <h1 className="text-2xl font-semibold">Auto-importación de siniestros</h1>
            <p className="text-sm text-muted">Correos recibidos que coincidieron con las reglas activas. Auto-escaneo cada 15 min.</p>
          </div>
        </div>
        <button
          onClick={triggerIntake}
          disabled={polling}
          data-testid="intake-trigger"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-md font-semibold inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowsClockwise className={`w-4 h-4 ${polling ? "animate-spin" : ""}`} weight="bold" />
          {polling ? "Escaneando…" : "Auto-importar ahora"}
        </button>
      </header>

      {/* Reglas activas */}
      <section className="bg-white rounded-lg border border-stone-200 p-4">
        <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <FunnelSimple className="w-4 h-4 text-stone-500" weight="bold" />
          Reglas activas
        </h2>
        {rules.length === 0 ? (
          <p className="text-sm text-muted">No hay reglas configuradas.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {rules.map((r) => (
              <li key={r.id} className="flex items-center gap-2 flex-wrap">
                <span className={`w-2 h-2 rounded-full ${r.active ? "bg-green-500" : "bg-stone-300"}`} />
                <span className="font-semibold">{r.name}</span>
                {r.type === "ms_booking" && (
                  <span className="text-[10px] font-semibold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" data-testid={`rule-type-badge-${r.id}`}>
                    Bookings
                  </span>
                )}
                <span className="text-xs text-muted">
                  · de: <code className="font-mono bg-stone-100 px-1 rounded">{(r.from_contains || []).join(", ")}</code>
                  {r.subject_contains?.length > 0 && (
                    <> · asunto contiene: <code className="font-mono bg-stone-100 px-1 rounded">{r.subject_contains[0]}</code></>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total registrados" value={stats.total} onClick={() => setFilter("all")} active={filter === "all"} />
        <StatCard label="Importados" value={stats.imported} accent="green" onClick={() => setFilter("imported")} active={filter === "imported"} />
        <StatCard label="Sin PDF" value={stats["no-pdf"]} accent="amber" onClick={() => setFilter("no-pdf")} active={filter === "no-pdf"} />
        <StatCard label="Duplicados" value={stats.duplicate} accent="stone" onClick={() => setFilter("duplicate")} active={filter === "duplicate"} />
      </section>

      {/* Log */}
      <section className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <header className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">Histórico ({filtered.length})</h2>
          <button
            onClick={refresh}
            data-testid="intake-refresh"
            className="text-xs text-stone-600 hover:text-foreground inline-flex items-center gap-1"
          >
            <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Refrescar
          </button>
        </header>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">
            <FileArrowDown className="w-10 h-10 mx-auto mb-2 opacity-30" weight="duotone" />
            {filter === "all" ? "Aún no se ha procesado ningún correo." : `Ningún correo con estado "${filter}".`}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="thead-modern">
                <tr>
                  <th>Fecha</th>
                  <th>Remitente</th>
                  <th>Asunto</th>
                  <th>Regla</th>
                  <th className="!text-center">Estado</th>
                  <th>Expedientes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <LogRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, accent, onClick, active }) {
  const colors = {
    green: "border-green-200 bg-green-50",
    amber: "border-amber-200 bg-amber-50",
    stone: "border-stone-200 bg-stone-50",
  };
  return (
    <button
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`text-left p-3 rounded-md border transition-colors ${
        active ? "border-brand ring-2 ring-brand/20" : colors[accent] || "border-stone-200 bg-white"
      } hover:border-brand`}
    >
      <div className="text-xs text-stone-600">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </button>
  );
}

function LogRow({ row }) {
  const meta = STATUS_META[row.status || "imported"] || STATUS_META.imported;
  const Icon = meta.icon;
  const created = row.created_at ? new Date(row.created_at) : null;
  return (
    <tr className="border-t border-[#F1F3F5] hover:bg-[#F5F6F8] transition odd:bg-white even:bg-[#FBFBFC]">
      <td className="py-2.5 px-3 text-xs tabular-nums text-muted whitespace-nowrap">
        {created ? created.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
      </td>
      <td className="py-2.5 px-3 text-xs">{row.from || "—"}</td>
      <td className="py-2.5 px-3 text-xs max-w-md truncate" title={row.subject}>{row.subject || "—"}</td>
      <td className="py-2.5 px-3 text-xs"><code className="bg-[#F1F3F5] px-1.5 py-0.5 rounded text-[11px]">{row.rule_id || "—"}</code></td>
      <td className="py-2.5 px-3 text-center">
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${meta.bg}`}>
          <Icon className="w-3 h-3" weight="fill" />
          {meta.label}
        </span>
      </td>
      <td className="py-2 px-3 text-xs">
        {(row.expediente_ids || []).length === 0 ? (
          <span className="text-stone-400">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.expediente_ids.map((eid) => (
              <Link
                key={eid}
                to={`/review/${eid}`}
                className="inline-flex items-center gap-1 bg-brand/10 text-brand hover:bg-brand/20 px-2 py-0.5 rounded font-mono"
                data-testid={`exp-link-${eid}`}
              >
                {eid.slice(0, 8)} <ArrowSquareOut className="w-3 h-3" weight="bold" />
              </Link>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
